import Fuse from 'fuse.js';

/**
 * @param {Array} brands - Array of brand objects
 * @param {Object} searchCriteria - Object with brandName and/or domain
 * @returns {Object|null} Best matching brand option or null
 */
export const findBestMatchingBrand = (brands, { brandName, domain }) => {
  if (!brandName && !domain) {
    return null;
  }

  const fuse = new Fuse(brands, {
    keys: ['name', 'domain'],
    threshold: 0.5
  });

  const searchTerms = {};

  if (brandName) {
    searchTerms.name = brandName;
  }

  if (domain) {
    searchTerms.domain = domain;
  }

  const results = fuse.search(searchTerms);

  if (results.length > 0) {
    return {
      value: results[0].item.id,
      label: results[0].item.name
    };
  }

  return null;
};

/**
 * @param {Array} brands - Array of brand objects
 * @param {number} brandId - Brand ID to find
 * @returns {Object|null} Brand option or null
 */
export const findBrandById = (brands, brandId) => {
  const brand = brands.find(b => b.id === brandId);
  if (brand) {
    return {
      value: brand.id,
      label: brand.name
    };
  }
  return null;
};

/**
 * @param {Array} brands - Array of brand objects
 * @returns {Array} Array of brand options
 */
export const transformBrandsToOptions = brands => {
  if (!brands) {
    return [];
  }

  return brands
    .sort((a, b) => {
      if (a.name && !b.name) {
        return -1;
      } else if (!a.name && b.name) {
        return 1;
      } else if (a.name && b.name) {
        if (a.domain && !b.domain) {
          return -1;
        } else if (!a.domain && b.domain) {
          return 1;
        }
        return a.name < b.name ? -1 : 1;
      }
      return a.domain < b.domain ? -1 : 1;
    })
    .map(brand => ({
      value: brand.id,
      label: getBrandNameWithDomain(brand)
    }));
};

export const getBrandNameWithFallback = brand => {
  return brand ? brand.name || brand.domain : '';
};

export const getBrandNameWithDomain = brand => {
  if (!brand) {
    return '';
  }
  if (!brand.name && brand.domain) {
    return brand.domain;
  }
  return brand.name + (brand.domain ? ` (${brand.domain})` : '');
};

/**
 * @param {Object} product - Product object
 * @param {Array} brands - Array of available brands
 * @returns {Object|null} Initial brand selection
 */
export const initializeBrandSelection = (product, brands) => {
  if (product.brandId) {
    return findBrandById(brands, product.brandId);
  }

  return findBestMatchingBrand(brands, {
    brandName: product.brandName,
    domain: product.domain
  });
};
